// steps/MengeStep.tsx

import React, { Fragment, useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

import { useAllowScroll } from "../AllowScrollContext";
import { ArrowLeft, Check, RotateCw, Weight } from "lucide-react";
import { MaterialSummary, SummaryContainer } from "./Summaries";
import { MaterialOrder } from "@/components/planning/Project";
import { ActionItem } from "@/shared/sharedService/ActionItem";
import { OpenInNew, OpenInNewOff } from "@mui/icons-material";
import CalculatorTextField from "../../CalculatorTextField";

export default function MengeStep({
  order,
  onBack,
  onSelect,
  expertMode,
  isDispo,
}: {
  order: MaterialOrder;
  isDispo: boolean;
  expertMode: boolean;
  onBack: () => void;
  onSelect: (patch: Partial<MaterialOrder>) => void;
}) {
  const { setAllowScroll } = useAllowScroll();

  useEffect(() => {
    setAllowScroll(false);
  }, [setAllowScroll]);

  const [amountValue, setAmountValue] = useState(order.amount);
  const [roundsUnit, setRoundsUnit] = useState(order.roundsUnit);
  const [showAmountInTransplan, setShowAmountInTransplan] = useState(order.showAmountInTransplan);
  const [saving, setSaving] = useState(false);

  const unit = order.article?.unit ?? "t";

  const save = () => {
    onSelect({ amount: amountValue, roundsUnit, showAmountInTransplan });
  };

  const handleContinue = () => {
    setSaving(true);
    save();
    setSaving(false);
  };

  useEffect(() => {
    if (!expertMode) return;
    save();
  }, [amountValue, roundsUnit, showAmountInTransplan, expertMode]);

  const currentShowAIT = showAmountInTransplan === true || (showAmountInTransplan === null && amountValue < 27);
  const isBaustellendienst = order.amount === -1 && !order.article;
  const isBauhof = !!order.article && order.article.bauhof > 0;

  return (
    <Stack spacing={1}>
      {!expertMode ? (
        <Fragment>
          <SummaryContainer>
            <MaterialSummary order={order} isBauhof={isBauhof} isBaustellendienst={isBaustellendienst} />
          </SummaryContainer>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography sx={{ fontSize: 18 }}>Menge festlegen</Typography>
          </Box>
        </Fragment>
      ) : null}

      {!order.article ? (
        <Typography sx={{ color: "text.secondary", mb: 1 }}>Hinweis: Noch kein Material ausgewählt. Bitte zuerst Material wählen.</Typography>
      ) : null}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        <Stack spacing={0} sx={{ mb: 0, flex: 1 }}>
          <Typography variant="button" sx={{ userSelect: "none" }}>
            {roundsUnit ? "Anzahl Touren" : `Menge in ${unit}`}
          </Typography>

          <CalculatorTextField
            placeholder={roundsUnit ? "Anzahl hier eingeben" : "Menge hier eingeben"}
            value={amountValue}
            setValue={setAmountValue}
          />
        </Stack>

        <Box>
          <Typography variant="button">Einheit</Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mb: 1,
            }}
          >
            <ActionItem
              size={expertMode ? "small" : undefined}
              color={roundsUnit ? undefined : "blue"}
              onPress={() => setRoundsUnit(false)}
              icon={Weight}
              label={order.article?.unit ?? "Tonnen"}
            />
            <ActionItem color={!roundsUnit ? undefined : "blue"} onPress={() => setRoundsUnit(true)} icon={RotateCw} label="Touren" />
          </Box>
        </Box>
        {isDispo ? (
          <Box>
            <Typography variant="button">Im Logistikplan anzeigen?</Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                mb: 1,
              }}
            >
              <ActionItem color={!currentShowAIT ? undefined : "blue"} onPress={() => setShowAmountInTransplan(true)} icon={OpenInNew} label="Ja" />
              <ActionItem
                color={currentShowAIT ? undefined : "blue"}
                onPress={() => setShowAmountInTransplan(false)}
                icon={OpenInNewOff}
                label="Nein"
              />
            </Box>
          </Box>
        ) : null}
      </Box>
      {expertMode ? null : saving ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <Button variant="contained" onClick={onBack} sx={{ minWidth: 44 }} startIcon={<ArrowLeft />}>
            Zurück
          </Button>

          <Button
            variant="contained"
            color="success"
            fullWidth
            startIcon={<Check />}
            onClick={handleContinue}
            disabled={amountValue === 0}
            sx={{
              ...(amountValue === 0 ? { opacity: 0.7 } : null),
            }}
          >
            Weiter
          </Button>
        </Box>
      )}
    </Stack>
  );
}
